import Str from "../../../helpers/Str";

export type BoxedTableColumn<T = { [key: string]: any }> = {
  title: string;
  className?: string;
  columnGroupClassName?: string;
  sharedClassName?: string;
  key?: keyof T;
  disabled?: boolean;
  render?: (item: T) => React.ReactNode;
  footer?: (items: T[]) => React.ReactNode;
};

export type BoxedTableProps<T = { [key: string]: any }> = {
  columns: BoxedTableColumn<T>[];
  items: T[];
  className?: string;
};

export function BoxedTable<T = { [key: string]: any }>(
  props: BoxedTableProps<T>
) {
  const groupColumn = props.columns[0];
  const groups = [
    ...Array.from(
      new Set(props.items.map((item) => (item as any)[groupColumn.key]))
    ),
  ];

  function getGroupItems(group: any) {
    return props.items.filter(
      (item) => (item as any)[groupColumn.key] === group
    );
  }

  return (
    <div
      data-component="BoxedTable"
      className={Str.tw("flex gap-1 flex-1 w-full", props.className)}
    >
      {props.columns.map((column, i) => (
        <div
          key={"title" + i}
          className={Str.tw(
            `w-full flex flex-col gap-1`,
            column.columnGroupClassName
          )}
        >
          <ColCell
            disabled={column.disabled}
            className={Str.tw(column.className, column.sharedClassName)}
          >
            {column.title}
          </ColCell>

          {groups.map((group, m) => (
            <div
              key={"group" + group + i + m}
              className="flex flex-col gap-1 relative"
              data-group={group}
            >
              {getGroupItems(group).map((item, j) => (
                <div key={"up" + i + j + m}>
                  {j === 0 && i === 0 && (
                    <Cell
                      disabled={column.disabled}
                      className={Str.tw(
                        `${i === 0 && j > 0 ? "opacity-0" : ""} ${
                          i === 0 && j === 0 ? "absolute inset-0" : ""
                        } flex justify-center items-center`,
                        column.sharedClassName
                      )}
                    >
                      {group}
                    </Cell>
                  )}

                  <Cell
                    disabled={column.disabled}
                    className={Str.tw(
                      `${i === 0 ? "opacity-0" : ""}`,
                      column.sharedClassName
                    )}
                  >
                    {column.render
                      ? column.render(item)
                      : column.key
                      ? item[column.key]
                      : ""}
                  </Cell>
                </div>
              ))}
            </div>
          ))}

          {column.footer && (
            <ColCell
              disabled={column.disabled}
              className={Str.tw(column.className, column.sharedClassName)}
            >
              {column.footer(props.items)}
            </ColCell>
          )}
        </div>
      ))}
    </div>
  );
}

const Cell = (props: {
  disabled?: boolean;
  className?: string;
  children: any;
}) => {
  return (
    <div
      className={Str.tw(
        "border rounded p-0.5 px-1 truncate text-center min-h-[30px]",
        props.className,
        props.disabled ? "opacity-50 border-gray-300" : "border-black"
      )}
    >
      {props.children}
    </div>
  );
};

const ColCell = (props: {
  disabled?: boolean;
  className?: string;
  children: any;
}) => {
  return (
    <div
      className={Str.tw(
        "border rounded p-0.5 px-1 font-bold text-center truncate min-h-[30px]",
        props.className,
        props.disabled ? "opacity-50 border-gray-300" : "border-black"
      )}
    >
      {props.children}
    </div>
  );
};
